/** biome-ignore-all lint/suspicious/noArrayIndexKey: <explanation> */
/** biome-ignore-all lint/a11y/useButtonType: <explanation> */
"use client";

import { ProductCardSkeleton } from "@/app/produkter/[[...slugs]]/components/ClientPLP";
import Search from "@/assets/svg/24/Search";
import Close from "@/assets/svg/32/Close";
import { AlgoliaSearch } from "@/components/atoms/AlgoliaSearch";
import { TextField } from "@/components/atoms/TextField";
import { ProductCard } from "@/components/molecules/ProductCard";
import { clsx } from "@frend-digital/ui";
import {
	useDebouncedCallback,
	useIntersectionObserverAPI,
} from "@frend-digital/ui/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import type { AlgoliaProductCard } from "@raumagarn/formatters";
import { useAtomValue } from "jotai";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import Modal from "../../../../components/organisms/Modal";
import headerStyles from "../header.module.css";
import useSearchModal from "../useSearchModal";
import styles from "./SearchModal.module.css";
import { activeTabAtom, SearchTabs } from "./SearchTabs";

const Sentinel = ({ action }: { action: () => void }) => {
	const obserrver = useIntersectionObserverAPI(([entry]) => {
		if (entry?.isIntersecting) {
			action();
		}
	});

	return (
		<span
			className={styles.sentinel}
			ref={(element) => {
				obserrver?.observe(element!);
				return () => {
					obserrver?.disconnect();
				};
			}}
		/>
	);
};

const searchSchema = z.object({
	search: z.string().min(1, "Søk kan ikke være tom"),
});

type SearchFormData = z.infer<typeof searchSchema>;

export const SearchModal = () => {
	const { isOpen, closeModal, mode, setMode } = useSearchModal();

	const { register, handleSubmit, watch, setValue } = useForm<SearchFormData>({
		resolver: zodResolver(searchSchema),
		defaultValues: { search: "" },
	});

	const searchValue = watch("search");
	const hasInput = !!searchValue?.trim();
	const lastSearchedValueRef = useRef<string>("");

	const [debouncedSearchValue, setDebouncedSearchValue] = useState("");

	const updateDebouncedSearch = useDebouncedCallback((value: string) => {
		setDebouncedSearchValue(value);
	}, 300);

	useEffect(() => {
		updateDebouncedSearch(searchValue || "");
	}, [searchValue, updateDebouncedSearch]);

	const onSubmit = (data: SearchFormData) => {
		setMode("searched");
		lastSearchedValueRef.current = data.search;
	};

	useEffect(() => {
		if (mode === "searched" && searchValue !== lastSearchedValueRef.current) {
			setMode("default");
		}
	}, [searchValue, mode, setMode]);

	return (
		<Modal isOpen={isOpen} callback={closeModal} className={styles.root}>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className={styles.header}>
					<div className={styles.closeIcon}>
						<Close onClick={closeModal} />
					</div>

					<TextField
						{...register("search")}
						className={styles.searchInput}
						classNames={{
							icon: styles.searchIcon,
						}}
						placeholder="Hva ser du etter?"
						icon={<Search />}
						clearIcon={hasInput}
						onClear={() => {
							setValue("search", "");
						}}
					/>
				</div>
			</form>
			<AlgoliaSearch value={debouncedSearchValue}>
				{({
					products,
					cardRecords,
					results,
					error,
					isLoading,
					autoCompleteSuggestions,
					popularSearches,
					fetchNextPage,
					isFetchingNextPage,
					hasMoreProducts,
				}) => {
					return (
						<div className={styles.content}>
							{!hasInput && popularSearches && popularSearches?.length > 0 && (
								<div className={styles.reccomendedSearches}>
									{popularSearches?.map((search, i) => (
										<button
											key={i}
											onClick={() => {
												setValue("search", search);
												handleSubmit(onSubmit)();
											}}
											className={styles.reccomendedSearch}
										>
											{search}
										</button>
									))}
								</div>
							)}
							{hasInput &&
								mode === "default" &&
								autoCompleteSuggestions.length > 0 && (
									<div className={styles.autoCompleteSuggestions}>
										<p
											className={clsx(styles.headerText, styles.active)}
											style={{ marginBottom: "0px" }}
										>
											Forslag
										</p>
										<div className={styles.suggestions}>
											{autoCompleteSuggestions.map((suggestion, i) => (
												<button
													key={i}
													onClick={() => {
														setValue("search", suggestion);
														handleSubmit(onSubmit)();
													}}
													className={styles.autoCompleteSearch}
												>
													{suggestion}
												</button>
											))}
										</div>
									</div>
								)}
							<SearchResults
								error={error}
								isLoading={isLoading}
								products={products}
								cardRecords={cardRecords}
								results={results}
								mode={mode}
								hasInput={hasInput}
								searchValue={searchValue}
								fetchNextPage={fetchNextPage}
								isFetchingNextPage={isFetchingNextPage}
								hasQuery={!!searchValue.trim()}
								hasMoreProducts={hasMoreProducts}
							/>
						</div>
					);
				}}
			</AlgoliaSearch>
		</Modal>
	);
};

const SearchResultsSkeleton = ({ size = 8 }: { size?: number }) => {
	return (
		<>
			{Array.from({ length: size })
				.fill(null)
				?.map((_, i) => (
					<ProductCardSkeleton key={i} />
				))}
		</>
	);
};

const SearchResults = ({
	error,
	isLoading,
	products,
	cardRecords,
	results,
	mode,
	hasInput,
	searchValue,
	fetchNextPage,
	isFetchingNextPage,
	hasQuery,
	hasMoreProducts,
}: {
	error: Error | null;
	isLoading: boolean;
	products?: AlgoliaProductCard[];
	cardRecords?: Record<string, AlgoliaProductCard[]>;
	results: number;
	mode: string;
	hasInput: boolean;
	searchValue: string;
	fetchNextPage: () => void;
	isFetchingNextPage: boolean;
	hasQuery: boolean;
	hasMoreProducts: boolean;
}) => {
	const activeTab = useAtomValue(activeTabAtom);
	if (error) return null;

	if (isLoading) {
		return (
			<div className={styles.products}>
				<SearchResultsSkeleton />
			</div>
		);
	}

	if (mode === "default") {
		return (
			<>
				<p className={clsx(styles.headerText, hasInput && styles.active)}>
					{hasInput ? "Produkter" : "Mest populært nå:"}
				</p>
				<div className={styles.products}>
					{products?.map((product, i) => (
						<ProductCard productCard={product} key={i} />
					))}
					{hasQuery && isFetchingNextPage && <SearchResultsSkeleton />}
					{hasMoreProducts && <Sentinel action={fetchNextPage} />}
				</div>
			</>
		);
	}

	return (
		<>
			<p className={styles.searchResultsHeader}>
				Viser treff på &quot;{searchValue}&quot;
			</p>
			<SearchTabs cardRecords={cardRecords} results={results} />
			<div className={styles.products}>
				{products
					?.filter(
						(product) =>
							product.productType === activeTab || activeTab === "all",
					)
					?.map((product, i) => (
						<ProductCard productCard={product} key={i} />
					))}
				{isFetchingNextPage && <SearchResultsSkeleton />}
				{hasMoreProducts && <Sentinel action={fetchNextPage} />}
			</div>
		</>
	);
};

export const SearchModalTrigger = () => {
	const { toggleModal } = useSearchModal();

	return (
		<button
			className={clsx(headerStyles.icon, headerStyles.search)}
			onClick={toggleModal}
		>
			<Search />
		</button>
	);
};
